import React from 'react';
import { Collapse } from 'antd';
import ConfigToggleOption from '../../baseComponents/ConfigToggleOption';
import type { FormInstance } from 'antd/es/form';
import { useBuilderSettingContext } from '../../../sampleApp/src/context/BuilderSettingsContext';

const { Panel } = Collapse;

/**
 * Props for the CallFeatures component.
 */
interface CallFeaturesProps {
  /** Ant Design form instance for handling form state. */
  form: FormInstance;
}

/**
 * CallFeatures component for managing voice and video calling settings.
 *
 * @param {CallFeaturesProps} props - The component props.
 * @returns {JSX.Element} The rendered CallFeatures component.
 */
const CallFeatures: React.FC<CallFeaturesProps> = ({ form }) => {
  const { callFeatures, setCallFeatures } = useBuilderSettingContext();

  /**
   * Handles toggling of call features.
   *
   * @param {string} type - The feature type to toggle.
   * @param {boolean} value - The new state of the feature.
   */
  const handleFeatureToggle = (type: string, value: boolean) => {
    form.setFieldsValue({
      callFeatures: {
        ...form.getFieldValue('callFeatures'),
        voiceAndVideoCalling: {
          ...form.getFieldValue(['callFeatures', 'voiceAndVideoCalling']),
          [type]: value,
        },
      },
    });
    setCallFeatures(form.getFieldValue('callFeatures'));
  };

  return (
    <div id="callFeatureWrapper" className="callFeatureWrapper">
      <div className="featureHeader callFeaturesHeader">Call Features</div>
      <Collapse
        className="commonCollapse callCollapse"
        defaultActiveKey={['calling']}
        bordered={false}
        expandIconPosition="end"
        expandIcon={({ isActive }) =>
          isActive ? <div className="panelHeaderCollapseIcon" /> : <div className="panelHeaderExpandIcon" />
        }
      >
        <Panel header={<div className="panelHeader callPanelHeader">Voice & Video Calling</div>} key="calling">
          <ConfigToggleOption
            name={['callFeatures', 'voiceAndVideoCalling', 'oneOnOneVoiceCalling']}
            label="One-on-one voice calling"
            isFeatureEnabled={callFeatures?.voiceAndVideoCalling?.oneOnOneVoiceCalling}
            onChange={(value) => handleFeatureToggle('oneOnOneVoiceCalling', value)}
          />
          <ConfigToggleOption
            name={['callFeatures', 'voiceAndVideoCalling', 'oneOnOneVideoCalling']}
            label="One-on-one video calling"
            isFeatureEnabled={callFeatures?.voiceAndVideoCalling?.oneOnOneVideoCalling}
            onChange={(value) => handleFeatureToggle('oneOnOneVideoCalling', value)}
          />
          <ConfigToggleOption
            name={['callFeatures', 'voiceAndVideoCalling', 'groupVoiceConference']}
            label="Group voice conference"
            isFeatureEnabled={callFeatures?.voiceAndVideoCalling?.groupVoiceConference}
            onChange={(value) => handleFeatureToggle('groupVoiceConference', value)}
          />
          <ConfigToggleOption
            name={['callFeatures', 'voiceAndVideoCalling', 'groupVideoConference']}
            label="Group video conference"
            isFeatureEnabled={callFeatures?.voiceAndVideoCalling?.groupVideoConference}
            onChange={(value) => handleFeatureToggle('groupVideoConference', value)}
          />
        </Panel>
      </Collapse>
    </div>
  );
};

export default CallFeatures;
